import { IHttpService } from "../HttpService";
import ReportingPeriod, {
  IReportingPeriod
} from "../../model/reporting/ReportingPeriod";
import { formatBooleanValue, parseBooleanValue } from "../..";
import ReportingPeriodListMapper from "./mappers/ReportingPeriodListMapper";
import ApplicantReportingPeriod, {
  IApplicantReportingPeriod
} from "../../model/applicant/ApplicantReportingPeriod";
import ReportingPeriodApplicant, {
  IReportingPeriodApplicant
} from "../../model/applicant/ReportingPeriodApplicant";
import { DateService } from "../DateService";
import AdditionalCheckDetails, {
  IAdditionalCheckDetails
} from "../../model/reporting/AdditionalCheckDetails";

export interface IReportingPeriodService {
  getReportingPeriods(): Promise<IReportingPeriod[]>;
  createReportingPeriod(reportingPeriod: IReportingPeriod): Promise<any>;
  updateReportingPeriod(reportingPeriod: IReportingPeriod): Promise<any>;
  assignApplicantsToReportingPeriod(reportingPeriod: IReportingPeriod): void;
  getApplicantReportingPeriod(
    applicantId: number,
    reportingPeriodId: number
  ): Promise<IApplicantReportingPeriod>;
  getReportingPeriod(reportingPeriodId: number): Promise<IReportingPeriod>;
  getReportingPeriodApplicants(
    reportingPeriodId: number
  ): Promise<IReportingPeriodApplicant[]>;
  isAllowedRandomSelection(reportingPeriodId: number): Promise<boolean>;
  selectForAdditionalCheck(
    reportingPeriodId: number,
    percentage: number,
    periodFrom?: Date,
    periodTo?: Date,
    min?: number,
    max?: number
  ): Promise<IAdditionalCheckDetails>;
  confirmAdditionalCheck(reportinPeriodId: number, executionId: number): void;
}

const ReportingPeriodService = (httpService): IReportingPeriodService => {
  const _http: IHttpService = httpService;
  const _basePath: string = "/api";
  const _reportingPeriodsPath: string = "/reporting-periods";
  const _applicantsPath: string = "applicants";
  const _randomSelectionPath: string = "random-selection";
  const _confirmPath: string = "confirm";

  const parseDateToTimestamp = (date?: Date) => {
    if (date) {
      return DateService().parseDateToTimestamp(date);
    }
  };

  const ReportingPeriodParamsMapper = (reportingPeriod: IReportingPeriod) => {
    return {
      id: reportingPeriod.id,
      reportingTypeCode: reportingPeriod.reportingTypeCode,
      name: reportingPeriod.name,
      valid: parseBooleanValue(reportingPeriod.valid),
      numberOfReportingPeriod: reportingPeriod.numberOfReportingPeriod,
      status: reportingPeriod.status,
      periodFrom: parseDateToTimestamp(reportingPeriod.periodFrom),
      periodTo: parseDateToTimestamp(reportingPeriod.periodTo)
    };
  };

  const ReportingPeriodApplicantsListMapper = (json: any = []) => {
    return json.map((item: any) => {
      let reportingPeriod = ReportingPeriodApplicant(item);
      reportingPeriod.publicationExists = formatBooleanValue(
        item.publicationExists
      );
      return reportingPeriod;
    });
  };

  return {
    async getReportingPeriods() {
      const path = _http.buildPath(_basePath, _reportingPeriodsPath);
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return ReportingPeriodListMapper(responseJSON);
    },

    async createReportingPeriod(reportingPeriod: IReportingPeriod) {
      const path = _http.buildPath(_basePath, _reportingPeriodsPath);
      const reponse = await _http.post(path, {
        params: ReportingPeriodParamsMapper(reportingPeriod)
      });
      return _http.toText(reponse);
    },

    async updateReportingPeriod(reportingPeriod: IReportingPeriod) {
      const path = _http.buildPath(
        _basePath,
        _reportingPeriodsPath,
        reportingPeriod.id.toString()
      );
      const response = await _http.put(path, {
        params: ReportingPeriodParamsMapper(reportingPeriod)
      });
      return _http.toText(response);
    },

    async assignApplicantsToReportingPeriod(reportingPeriod: IReportingPeriod) {
      const path = _http.buildPath(
        _basePath,
        _reportingPeriodsPath,
        reportingPeriod.id.toString(),
        _applicantsPath
      );
      const response = await _http.post(path, {
        params: reportingPeriod
      });
      return _http.toText(response);
    },

    async getApplicantReportingPeriod(
      applicantId: number,
      reportingPeriodId: number
    ) {
      const path = _http.buildPath(
        _basePath,
        _reportingPeriodsPath,
        reportingPeriodId.toString(),
        _applicantsPath,
        applicantId.toString()
      );
      const response = await _http.get(path);

      const responseJSON = await _http.toJSON(response);
      return ApplicantReportingPeriod(responseJSON);
    },

    async getReportingPeriod(reportingPeriodId: number) {
      const path = _http.buildPath(
        _basePath,
        _reportingPeriodsPath,
        reportingPeriodId.toString()
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return ReportingPeriod(responseJSON);
    },

    async getReportingPeriodApplicants(reportingPeriodId: number) {
      const path = _http.buildPath(
        _basePath,
        _reportingPeriodsPath,
        reportingPeriodId.toString(),
        _applicantsPath
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return ReportingPeriodApplicantsListMapper(responseJSON);
    },

    async isAllowedRandomSelection(reportingPeriodId: number) {
      const path = _http.buildPath(
        _basePath,
        _reportingPeriodsPath,
        reportingPeriodId.toString(),
        _randomSelectionPath
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return responseJSON;
    },

    async selectForAdditionalCheck(
      reportingPeriodId: number,
      percentage: number,
      periodFrom?: Date,
      periodTo?: Date,
      min?: number,
      max?: number
    ) {
      const path = _http.buildPath(
        _basePath,
        _reportingPeriodsPath,
        reportingPeriodId.toString(),
        _randomSelectionPath
      );
      const response = await _http.put(path, {
        form: true,
        params: {
          percentage,
          periodFrom: periodFrom?.getTime(),
          periodTo: periodTo?.getTime(),
          min,
          max
        }
      });
      const responseJSON = await _http.toJSON(response);
      return AdditionalCheckDetails(responseJSON);
    },

    async confirmAdditionalCheck(
      reportingPeriodId: number,
      executionId: number
    ) {
      const path = _http.buildPath(
        _basePath,
        _reportingPeriodsPath,
        reportingPeriodId.toString(),
        _randomSelectionPath,
        _confirmPath
      );
      const response = await _http.put(path, {
        form: true,
        params: { executionId }
      });
      return _http.toText(response);
    }
  };
};

export default ReportingPeriodService;
